import { BadgeProps, UseType } from './Badge';

import { BadgeContextConfig } from './BadgeProvider';
import { CSSObject } from 'foundations-theming/utils';
import { BorderRadius, Color, Theme } from 'foundations-theming';
import { deepMerge } from '../../utils/deepMerge';

const backgroundColorMapping: Partial<Record<UseType, Color>> = {
  /**
   * Overrides
   */
  calypso: 'fill-accent-blue-default',
  'candy-apple': 'fill-accent-red-default',
  koala: 'fill-accent-neutral-subtle-alt',
  lorax: 'fill-accent-orange-default',
  marigold: 'fill-caution-default',
  norman: 'fill-accent-magenta-default',
  oz: 'fill-accent-green-default',
  pantera: 'fill-accent-neutral-default',
  sorbet: 'fill-accent-light-orange-default',
  thunderdome: 'fill-accent-purple-default',
  /**
   * Custom
   */
  beta: 'fill-accent-purple-default',
  free: 'fill-accent-orange-default',
  new: 'fill-accent-green-default',
  blue: 'fill-accent-blue-subtle',
  teal: 'fill-accent-teal-subtle',
  'teal-dark': 'fill-accent-teal-default',
  green: 'fill-accent-green-subtle',
  yellow: 'fill-accent-yellow-subtle',
  orange: 'fill-accent-orange-subtle',
  magenta: 'fill-accent-magenta-subtle',
  purple: 'fill-accent-purple-subtle',
  red: 'fill-accent-red-subtle',
};

const addBorderRadiusCornerValue = ({
  theme,
  styles,
  borderRadiusKey,
  cornerRadius,
  borderRadius,
}: {
  theme: Theme;
  styles: CSSObject;
  borderRadiusKey: string;
  cornerRadius?: BorderRadius;
  borderRadius?: BorderRadius;
}) => {
  // Default to the overall borderRadius if individual values are undefined
  const value = cornerRadius ?? borderRadius;
  if (value) {
    styles[borderRadiusKey] = theme.borderRadius[value];
  }
};

export const getBackgroundColorToken = (
  use: NonNullable<BadgeProps['use']>
): Color => backgroundColorMapping[use] ?? 'fill-accent-orange-default';

export const getBadgeStyleOverrides = ({
  use,
  theme,
  borderRadius,
  borderStartStartRadius,
  borderStartEndRadius,
  borderEndStartRadius,
  borderEndEndRadius,
  badgeContext,
}: {
  use: string;
  theme: Theme;
  borderRadius?: BorderRadius;
  borderStartStartRadius?: BorderRadius;
  borderStartEndRadius?: BorderRadius;
  borderEndStartRadius?: BorderRadius;
  borderEndEndRadius?: BorderRadius;
  badgeContext?: BadgeContextConfig;
}): CSSObject => {
  const styles = {};

  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderStartStartRadius',
    cornerRadius: borderStartStartRadius,
    borderRadius,
  });
  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderStartEndRadius',
    cornerRadius: borderStartEndRadius,
    borderRadius,
  });
  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderEndStartRadius',
    cornerRadius: borderEndStartRadius,
    borderRadius,
  });
  addBorderRadiusCornerValue({
    theme,
    styles,
    borderRadiusKey: 'borderEndEndRadius',
    cornerRadius: borderEndEndRadius,
    borderRadius,
  });

  return deepMerge(
    {
      ...(use !== 'count' ? styles : {}),
    },
    badgeContext?.badgeStyleOverrides ?? {}
  );
};
